import {
  AfterViewInit,
  Component, computed,
  ElementRef,
  HostListener,
  inject,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ThemeService } from './services/theme.service';
import { BroadcastService } from './services/broadcast.service';
import { Subscription } from 'rxjs';
import { AuthService } from './services/auth.service';
import { UserService } from './services/user.service';
import { ApiService } from './services/api.service';
import { ConfigService } from './services/config.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, NavigationEnd, NavigationStart, Params, Router, RouterEvent } from '@angular/router';
import { ResultsComponent } from './components/results/results.component';
import { WelcomeComponent } from './components/welcome/welcome.component';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { ResizeService } from './services/resize.service';
import { AnalyticsService } from './services/analytics.service';
import { filter, finalize } from 'rxjs/operators';
import { LocaleService } from './services/locale.service';
import { InfoComponent } from './components/info/info.component';
import { LoggerService } from './services/logger.service';
import { HomeComponent } from './components/home/home.component';
import { TutorsDialogComponent } from './components/tutors-dialog/tutors-dialog.component';
import { InterestsDialogComponent } from './components/interests-dialog/interests-dialog.component';
import { GeolocationService } from './services/geolocation.service';
import { CookiePolicyComponent } from './components/cookie-policy/cookie-policy.component';
import { Util } from './util';
import { BlankComponent } from './components/blank/blank.component';
import { LoginCallbackComponent } from './components/login/login-callback/login-callback.component';
import { LogoutConfirmationComponent } from './components/logout-confirmation/logout-confirmation.component';
import { LoginRemoteComponent } from './components/login/login-remote/login-remote.component';
import { Message } from './models/message';
import { Checkout } from './state/user/checkouts/models/checkout';
import { Hold } from './state/user/holds/models/hold';
import { IberiaLandingComponent } from './components/landings/iberia/iberia-landing.component';
import { DOCUMENT } from '@angular/common';
import { HeapService } from './services/heap.service';
import { FingerprintService } from './services/fingerprint.service';
import { MAX_ACTIVE_DEVICES_ALLOWED, VISITOR_ID } from './models/device';
import { JsonLDService } from 'src/app/services/jsonld.service';
import { GoogleTagManagerService } from './services/google-tag-manager.service';
import { Direction } from '@angular/cdk/bidi';
import { UrlStateService } from './services/url-state.service';
// import Keycloak from 'keycloak-js';
import { InterestedUserService } from './services/interested-user.service';
import { SEOService } from './services/seo.service';
import { RecommendPlatformDialog } from './components/dialogs/recommend-platform-dialog/recommend-platform-dialog.component';
import { MtnLandingComponent } from './components/landings/mtn/mtn-landing.component';

import { CollectionsFacade } from './state/collections/collections.facade';
import { UserConfigFacade } from './state/user-config/user-config.facade';
import { HoldsFacade } from './state/user/holds/holds.facade';
import { CheckoutsFacade } from './state/user/checkouts/checkouts.facade';
import { ActionType } from './models/action';
import { HeaderService } from './components/header/services/header.service';
import { InternalLoginPage } from './internal-login/page/internal-login.page';
import { AccessibilityFacade } from './_core/accessibility/state/accessibility.facade';
import { HighContrastMode } from './_core/accessibility/services/models/accesibility.model';
import { EditingFacade } from './_core/state/dialogs/editing/editing.facade';
import { LoginDialogComponent } from './login/components/login-dialog/login-dialog.component';
import { ToastService } from './_core/services/toast.service';
import { UserFacade } from './_core/state/user/user.facade';
import { ResetPasswordDialogComponent } from './_shared/components/dialogs/reset-password-dialog/reset-password-dialog.component';
import { CookiesFacade } from './_core/state/dialogs/cookies/cookies.facade';
import { toSignal } from '@angular/core/rxjs-interop';
import { AnalyticsManager } from './_core/analytics/analytics-manager';
import { AnalyticsService as NewAnalyticsService } from './_core/analytics/analytics.service';
import { FacetsFacade } from './state/facets/facets.facade';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements AfterViewInit, OnInit, OnDestroy {
  private resizeObserver?: ResizeObserver;
  private readonly analyticsManager = inject(AnalyticsManager);
  private readonly newAnalyticsService = inject(NewAnalyticsService);
  private readonly toastService = inject(ToastService);
  private readonly userFacade = inject(UserFacade);
  private readonly cookiesFacade = inject(CookiesFacade);
  private readonly facetsFacade = inject(FacetsFacade);

  readonly firstLoginConfig = signal(this.configService.get().firstLoginConfig);
  readonly firstLoginAnyConfigEnabled = computed(() => this.firstLoginConfig().message || this.firstLoginConfig().showEmailChange || this.firstLoginConfig().showPasswordChange || this.firstLoginConfig().showRutChange || this.firstLoginConfig().showTermsConditions);
  readonly showFirstLoginDialog = computed(() => this.firstLoginConfig().showFirstLoginDialog && this.firstLoginAnyConfigEnabled());
  readonly hasConsent = toSignal(this.cookiesFacade.hasConsent$);
  readonly showConfirmationEmailDialog = signal<boolean>(false);
  readonly confirmationEmailResult = signal<boolean>(false);
  readonly confirmationEmailLabel = signal<string>('');

  initError = false;
  openSidebar = false;
  isHome = false;
  isResultsPage = false;
  isRecordInfo = false;
  refreshFilters: boolean;
  withBanner: boolean;
  showDesigner: boolean;
  isBot: boolean;
  headerType = 'option-1';

  loginSub: Subscription;
  sidebarSub: Subscription;
  headerLogoSub: Subscription;

  assistantActive: boolean;
  shouldReduceMarginBottom: boolean;
  @ViewChild('containerTitle', { static: false }) titleH1: ElementRef;
  public direction: Direction;

  @ViewChild('recommendPlatformDialog') recommendPlatformDialog!: RecommendPlatformDialog;
  @ViewChild('resetPasswordDialog') resetPasswordDialog!: ResetPasswordDialogComponent;

  constructor(
    @Inject(DOCUMENT) private docEl,
    private logger: LoggerService,
    private localeService: LocaleService,
    private broadcastService: BroadcastService,
    private authService: AuthService,
    private userService: UserService,
    private themeService: ThemeService,
    private apiService: ApiService,
    private configService: ConfigService,
    private resizeService: ResizeService,
    private analyticsService: AnalyticsService,
    private googleTagManagerService: GoogleTagManagerService,
    private heapService: HeapService,
    private locationService: GeolocationService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private meta: Meta,
    private title: Title,
    private fingerprintService: FingerprintService,
    private jsonLDService: JsonLDService,
    private seoService: SEOService,
    private urlStateService: UrlStateService,
    private interestedUserService: InterestedUserService,
    private readonly collectionsFacade: CollectionsFacade,
    private readonly userConfigFacade: UserConfigFacade,
    private readonly holdsFacade: HoldsFacade,
    private readonly checkoutsFacade: CheckoutsFacade,
    private readonly accessibilityFacade: AccessibilityFacade,
    private readonly headerService: HeaderService,
    private readonly editingFacade: EditingFacade
  ) {
    this.isBot = Util.isBot();
    this.sidebarSub = broadcastService.sidebar$.subscribe((open) => (this.openSidebar = open));
    this.headerLogoSub = this.themeService.headerLogos$.subscribe(
      (logos) => (this.headerType = logos.length ? 'option-2' : 'option-1')
    );
    this.assistantActive = this.isAssistantActive();
    this.urlStateService.preserveUtmParams();
  }

  ngOnInit() {
    this.cookiesFacade.initializeCookiesFromStorage();
    this.analyticsService.init();
    this.googleTagManagerService.init();
    this.heapService.init();
    this.initError = this.configService.initError;
    this.adaptViewForLanding();
    this.facetsFacade.getFacets().subscribe();

    if (this.initError) {
      this.localeService
        .getString('messages.error.connectionNotAvailable')
        .subscribe((value) => this.title.setTitle(value));

      this.themeService.set('default');
      return this.logger.error('Init error!');
    }

    this.userFacade.user$.pipe(filter((user) => user !== undefined)).subscribe((user) => {
      this.analyticsManager.setUser({
        id: user.id,
        email: user.email,
        country: user.country,
      });
    });

    if (
      this.configService.isLearningRoomBecasSantander() &&
      !this.authService.isAuthenticated() &&
      window.location.pathname !== '/login/callback'
    ) {
      /*const keycloak = new Keycloak({
        url: 'https://sso.santanderopenacademy.com/auth',
        realm: 'Becas',
        clientId: 'odilo-client-connect',
      });

      keycloak
        .init({
          flow: 'implicit',
          checkLoginIframe: true,
          onLoad: 'check-sso',
          redirectUri: window.location.origin + '/login/callback',
          enableLogging: true,
        })
        .then(function (authenticated) {
          if (authenticated) {
            console.log('Successful auth from keycloak');
          }
        })
        .catch(function () {});*/
    }

    this.handleEzProxy();
    this.withBanner = this.configService.get().topBar.show;
    this.themeService.set(this.configService.get().theme);
    this.setDefaultTitle();
    if (this.shouldShowLanding()) {
      this.router.navigate(['landing']);
    }

    if (!this.isBot) {
      this.apiService.getAllFilters().subscribe((filters) => {
        const formatFilter = filters.find((filter) => filter.name === Util.FORMAT_FILTER);
        if (formatFilter) {
          this.configService.setAvailableExperiencesInfoDialogData(
            Util.getAvailableExperiencesInfoFromFormatFilter(formatFilter)
          );
        }

        if (window.location.pathname.includes('results')) {
          return;
        }

        this.broadcastService.broadcastFilters(filters);
      });

      this.loginSub = this.broadcastService.login$.subscribe(() => this.checkFirstLoginMessage());

      this.checkFirstLoginMessage();

      if (this.configService.get().showWelcome) {
        const welcomeVar = localStorage.getItem('welcome');

        if (!welcomeVar && !this.authService.isAuthenticated()) {
          setTimeout(() => {
            this.dialog
              .open(WelcomeComponent, {
                disableClose: true,
              })
              .afterClosed()
              .subscribe((goToLogin) => {
                if (goToLogin) {
                  this.dialog
                    .open(LoginDialogComponent)
                    .afterClosed()
                    .subscribe((result) => {
                      if (result) {
                        (window as any).location.reload();
                      } else {
                        const action = Util.getAction();
                        if (action) {
                          Util.setAction({
                            ...action,
                            type: ActionType.NONE,
                          });
                        } else {
                          Util.removeAction();
                        }
                      }
                    });
                }
              });
          });
        }
      }
    }

    if (this.authService.isAuthenticated()) {
      this.userService.getUserData();
      this.userFacade.loadProfilePicture(this.userFacade.user.id);
    }

    this.broadcastService.userData$.subscribe(() => this.handleUserDataEvent());

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (!this.router.navigated) {
          // page refresh
          this.collectionsFacade.clearStore();
          sessionStorage.removeItem(Util.BOOKCLUBS_KEY);
        }
      }

      if (event instanceof NavigationEnd) {
        const viewOrigin = document.referrer ? 'external_link' : 'user_navigation';
        this.newAnalyticsService.sendPageViewEvent(viewOrigin);
      }
    });

    this.route.queryParams.subscribe((params) => {
      if (params.chpwd && !this.configService.get().privateMode) {
        this.apiService.isChpwdValid(params.chpwd).subscribe((isValid) => {
          if (isValid) {
            this.resetPasswordDialog.openModal(params.chpwd);
          } else {
            this.toastService.showToast('messages.chpwd.expiredLink', 'error');
          }
        });
      } else if (params.id) {
        this.router.navigate(['/info', params.id]);
      } else if (params.v) {
        this.apiService.confirmUser(params.v).subscribe({
          next: () => {
            this.confirmationEmailResult.set(true);
            this.showConfirmationEmailDialog.set(true);
            this.confirmationEmailLabel.set('login.confirm.validated');
          },
          error: () => {
            this.confirmationEmailResult.set(true);
            this.showConfirmationEmailDialog.set(true);
            this.confirmationEmailLabel.set('login.confirm.notValidated');
          },
        });
      }

      this.openSignUpModal(params);
    });

    if (this.configService.get().geolocation) {
      this.locationService
        .getLocation()
        .subscribe((loc) =>
          this.apiService
            .validateLocation(loc)
            .subscribe((result) => this.broadcastService.broadcastUserLocation(result))
        );
    }

    if (this.configService.get().deviceControlViaWeb && this.authService.isAuthenticated()) {
      setTimeout(async () => {
        const visitorId = await this.fingerprintService.getVisitorId();
        this.apiService.getUserActiveDevices(this.userFacade.user.id).subscribe((devices) => {
          if (devices == null || devices.length === 0) {
            return;
          }
          const activeDevices = devices.filter((device) => device.active);
          const currentFound = activeDevices.find((device) => device.deviceId === visitorId) != null;
          if (activeDevices.length == MAX_ACTIVE_DEVICES_ALLOWED && !currentFound) {
            const previousVisitorId = localStorage.getItem(VISITOR_ID);

            if (visitorId !== previousVisitorId) {
              localStorage.setItem(VISITOR_ID, visitorId);
              location.reload();
            }
          }
        });
      }, 5000);
    }
    this.setBidirectionality();

    const osHighContrastMode = matchMedia('(prefers-contrast: more)').matches;
    const osLightColorScheme = matchMedia('(prefers-color-scheme: light)').matches;

    if (osHighContrastMode) {
      this.accessibilityFacade.setHighContrastEnabled(true);
      this.accessibilityFacade.setHighContrastMode(HighContrastMode.DARK);

      if (osLightColorScheme) {
        this.accessibilityFacade.setHighContrastMode(HighContrastMode.LIGHT);
      }
    }
  }

  ngAfterViewInit() {
    const loggedUser = this.userFacade.user;

    if (loggedUser) {
      (function (opts) {
        opts.traits = {
          user_id: loggedUser.id,
        };
      })(((window as any)._sva = (window as any)._sva || {}));
    }

    // Update page width css variable to handle margins
    this.resizeObserver = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--page-width', `${document.body.clientWidth}px`);
    });

    this.resizeObserver.observe(document.body);
  }

  ngOnDestroy() {
    this.loginSub.unsubscribe();
    this.sidebarSub.unsubscribe();
    this.headerLogoSub.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  @HostListener('window:beforeunload', ['$event'])
  onWindowBeforeUnload(event: BeforeUnloadEvent) {
    if (this.editingFacade.isEditing) {
      event.preventDefault();
      // Legacy support
      event.returnValue = 'Editing';
      return 'Editing';
    }
  }

  @HostListener('document:keydown.shift.alt.s')
  @HostListener('document:keydown.shift.meta.s')
  toggleDesigner() {
    this.showDesigner = !this.showDesigner;
  }

  @HostListener('document:keydown.shift.meta.l')
  toggleLogger() {
    this.logger.switch();
  }

  @HostListener('document:keydown.shift.alt.z')
  @HostListener('document:keydown.shift.meta.z')
  handleInternalLogin() {
    this.dialog.open(InternalLoginPage, {
      disableClose: false,
    });
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    if (event.origin.includes('nubereader')) {
      const message: Message = event.data;

      switch (message.type) {
        case 'NEW_CHECKOUT':
          return this.checkoutsFacade.checkouts.push(message.data as Checkout);
        case 'NEW_HOLD':
          return this.holdsFacade.holds.push(message.data as Hold);
        default:
          this.logger.warn('Unknown message type', message);
      }
    }
  }

  onLanguageSelected(lang: string) {
    if (this.authService.isAuthenticated()) {
      const user = this.userFacade.user;
      user.locale = lang;

      this.apiService
        .updateUser(user)
        .pipe(finalize(() => (window as any).location.reload()))
        .subscribe((_user) => this.authService.updateUser(_user));
    } else {
      localStorage.setItem('language', lang);
      (window as any).location.reload();
    }
  }

  checkFirstLoginMessage() {
    if (this.showFirstLoginDialog()) {
      setTimeout(() => {
        this.dialog.open(LoginDialogComponent, {
          data: { screen: 'first-login' },
          disableClose: true,
        });
      });
    }
  }

  onDeactivate(event: RouterEvent) {
    this.refreshFilters = event instanceof ResultsComponent;
  }

  loadMetatagsHome() {
    const keywordsKey = 'meta.keywords';
    const descriptionKey = 'meta.description';

    this.localeService.getString([keywordsKey, descriptionKey]).subscribe((values) => {
      const keywords = values[keywordsKey];
      const url = window.location.href.split('?')[0];
      const title = this.configService.get().title;
      const description = values[descriptionKey];
      const timeout = this.configService.devMode ? 2000 : 1000;

      const image = this.themeService.logoSeoUrl;
      let ogDescription;
      if (description.length > 52) {
        ogDescription = description.substr(0, 52).concat('...');
      }

      this.seoService.generateSEOTags(title, description, url, image, keywords);

      this.jsonLDService.removeStructuredData();
    });
  }

  onActivate(event: RouterEvent) {
    this.logger.info('AppComponent router', event);
    this.isResultsPage = event instanceof ResultsComponent;
    const shouldOpen = this.isResultsPage && !this.resizeService.isMobile;
    this.isHome = event instanceof HomeComponent || event instanceof MtnLandingComponent;
    this.isRecordInfo = event instanceof InfoComponent;

    this.setLanguageHeadInfo();

    if (this.refreshFilters) {
      this.apiService.getAllFilters().subscribe((filters) => this.broadcastService.broadcastFilters(filters));
    }

    this.broadcastService.broadcastSidebar(shouldOpen);

    if (this.isHome) {
      this.loadMetatagsHome();

      // Aula madrid needs to sidebar is open by default in homepage when user is in desktop devices.
      if (this.configService.get().codCliente === 'E1155' && window.outerWidth > 768) {
        this.broadcastService.broadcastSidebar(true);
      }
    }

    if (this.shouldDisplayCookiesDialog()) {
      // TODO: meh
      const showCookiesByComp =
        !(event instanceof CookiePolicyComponent) &&
        !(event instanceof LogoutConfirmationComponent) &&
        !(event instanceof LoginRemoteComponent) &&
        !(event instanceof LoginCallbackComponent) &&
        !(event instanceof IberiaLandingComponent) &&
        !(event instanceof MtnLandingComponent);

      if (showCookiesByComp) {
        this.ngZone.run(() => {
          // Blank page shows login modal just after this modal shows, so we need to add a delay
          // to prevent this modal showing before the login one just if is Blank page.
          setTimeout(
            () => {
              this.cookiesFacade.openCookiesDialog();
            },
            event instanceof BlankComponent ? 500 : 0
          );
        });
      }
    }
  }

  closeConfirmationEmailDialog() {
    this.showConfirmationEmailDialog.set(false);
    if (this.confirmationEmailResult()) {
      this.clearUrlAndLogin();
    }
  }

  handleResetPasswordDialog() {
    this.clearUrlAndLogin();
  }

  private openSignUpModal(params: Params) {
    const isRegister = params.register ? params.register.toLocaleLowerCase() === 'true' : false;
    const openSignUpModal = isRegister && this.configService.get().allowSignUp;
    this.interestedUserService.openSingUpModal(openSignUpModal);
  }

  private handleUserDataEvent() {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    if (this.shouldShowTutorsDialog()) {
      setTimeout(() => {
        this.dialog
          .open(TutorsDialogComponent, {
            disableClose: true,
          })
          .afterClosed()
          .subscribe((data) => {
            if (data) {
              this.apiService
                .setTutors(
                  this.userService.getUserId(),
                  data.emails.map((t: any) => t.email)
                )
                .subscribe((_tutors) => {
                  const user = this.userFacade.user;
                  user.tutorsFilled = true;

                  this.authService.updateUser(user);
                  this.userService.tutors = _tutors;
                  this.toastService.showToast('messages.user.update');
                });
            }
          });
      });
    }

    if (this.shouldShowInterestsDialog()) {
      setTimeout(() => {
        this.dialog.open(InterestsDialogComponent, {
          disableClose: true,
          data: this.configService.get().userInterests,
        });
      });
    }

    this.displayRecommendationDialogInfo();
  }

  private shouldShowTutorsDialog(): boolean {
    if (sessionStorage.getItem('tutorsDismiss')) {
      return false;
    }

    if (!this.authService.isAuthenticated() || this.userFacade.user.tutorsFilled) {
      return false;
    }

    const tutors = this.configService.get().tutors;
    const tutorsCentres = this.configService.get().tutorsCentres;
    const userCentre = this.userFacade.user.centre;
    const role = this.userFacade.user.role;

    if (!tutors) {
      // WENABLE_TUTORS
      return false;
    }
    // role is null or is Student-like
    if (!role || this.isStudentRole(role)) {
      return true;
    }
    // WENABLE_TUTORS and we already know if is a student role or not
    if (tutors && role) {
      return false;
    }
    return !tutorsCentres || tutorsCentres.includes(userCentre);
  }

  private isStudentRole(role: string) {
    switch (role.toUpperCase()) {
      case 'ALUMNO':
      case 'STUDENT':
      case 'ESTUDIANTE':
        return true;
      default:
        return false;
    }
  }

  private shouldShowInterestsDialog(): boolean {
    if (sessionStorage.getItem('interestsDismiss')) {
      return false;
    }

    if (!this.configService.get().showInterestsForm) {
      return false;
    }

    const interests = this.configService.get().userInterests;
    const hasInterestsConfig = interests && interests.active;
    const loggedUser = this.userFacade.user;

    return hasInterestsConfig && !loggedUser.interestsFilled;
  }

  private shouldShowLanding(): boolean {
    const showLanding = this.configService.get().showLanding;
    const landingVar = localStorage.getItem('landing');
    return showLanding && !landingVar;
  }

  private shouldDisplayCookiesDialog(): boolean {
    return !Util.isBot() && this.configService.get().showCookiesDisclaimer && !this.hasConsent();
  }

  private displayRecommendationDialogInfo() {
    this.userConfigFacade.recommendationsDialogInfo$
      .pipe(filter((recommendation) => recommendation?.showDialog))
      .subscribe(() => {
        this.recommendPlatformDialog.dialog.openDialog();
      });
  }

  private clearUrlAndLogin() {
    this.router.navigate(['/']);
    this.dialog
      .open(LoginDialogComponent)
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          const action = Util.getAction();
          if (action) {
            Util.setAction({
              ...action,
              type: ActionType.NONE,
            });
          } else {
            Util.removeAction();
          }
        } else {
          (window as any).location.reload();
        }
      });
  }

  private setDefaultTitle() {
    this.title.setTitle(this.configService.get().title || 'LetminoTK');
    if (this.titleH1) {
      this.titleH1.nativeElement.innerHTML = `<h1> ${this.title.getTitle()} </h1>`;
    }
  }

  private setLanguageHeadInfo() {
    const parsedUrl = new URL(this.docEl.location);
    const currentLocation = this.docEl.location.href;
    this.docEl.documentElement.lang = this.localeService.language;

    this.configService
      .get()
      .languages.filter((l) => l.length)
      .forEach((l) => {
        parsedUrl.searchParams.set('locale', l);
        this.getLanguageFun(l).apply(this, [l, parsedUrl.toString()]);
      });

    // fallback
    const fallback = 'x-default';
    this.getLanguageFun(fallback).apply(this, [fallback, currentLocation]);
  }

  private getLanguageFun(locale: string): Function {
    return this.existsLanguageHead(locale) ? this.updateLanguageHead : this.createLanguageHead;
  }

  private createLanguageHead(lang: string, href: string) {
    const link: HTMLLinkElement = this.docEl.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', lang);
    link.setAttribute('href', href);

    this.docEl.head.appendChild(link);
  }

  private existsLanguageHead(lang: string): boolean {
    return this.docEl.querySelector(`link[hreflang=${lang}]`);
  }

  private updateLanguageHead(lang: string, href: string) {
    this.docEl.querySelector(`link[hreflang=${lang}]`).href = href;
  }

  private setMetaTagsHome(
    title: string,
    description: string,
    ogDescription: string,
    keywords: string,
    url: string,
    image: string
  ) {
    const tags: MetaDefinition[] = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'language', content: this.localeService.language },
      { name: 'og:title', content: title },
      { name: 'og:description', content: ogDescription },
      { name: 'og:url', content: url },
      { name: 'og:image', itemprop: 'image', content: image },
      { name: 'og:type', content: 'website' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image },
    ];

    if (this.meta.getTag('name="description"')) {
      tags.forEach((t) => this.meta.updateTag(t));
    } else {
      this.meta.addTags(tags);
    }
  }

  private handleEzProxy() {
    const requestHeaders = (window as any).__REQUEST_HEADERS__;
    const queryParams = new URLSearchParams(window.location.search);
    const app = queryParams.get('app');
    const redirectTo = queryParams.get('redirectTo');

    if (requestHeaders && !this.authService.isAuthenticated()) {
      requestHeaders['app'] = app;

      const client = new URLSearchParams(window.location.search).get('client');
      if (client === 'app') {
        const idUser = requestHeaders['x-username'];
        let url = `odilo://generic.app?X-Username=${idUser}`;

        if (this.configService.isIteso()) {
          url = `iteso://generic.app?X-Username=${idUser}`;
        }

        window.location.href = url;
      } else if (app === 'bc') {
        const clubId = queryParams.get('clubId');
        this.apiService.getLinkToBookclub({ clubId, xUsername: requestHeaders['x-username'] }).subscribe((url) => {
          window.location.href = url;
        });
      } else {
        this.authService.externalLogin(requestHeaders).subscribe((user) => {
          this.userService.getUserData();
          this.broadcastService.broadcastLogin(true);
          this.authService.loadProfilePicture(user).subscribe();
          sessionStorage.removeItem('isLoggingIn');

          if (redirectTo) {
            window.location.href = window.location.origin + redirectTo;
          } else {
            window.location.reload();
          }
        });
      }
    }
  }

  private isAssistantActive(): boolean {
    return this.configService.get() && this.configService.get().assistant && this.configService.get().assistant.active;
  }

  private adaptViewForLanding() {
    Util.getCurrentRoute(this.router).then((route) => (this.shouldReduceMarginBottom = route === '/landing'));
  }

  private setBidirectionality() {
    this.docEl.documentElement.dir = this.localeService.getTextDirection();
  }
}
